import ApolloClient from 'apollo-client';
import { GRAPHQL } from '../../config/api_end_point';
import { auth } from './Routes';
import 'cross-fetch/polyfill';
import { createUploadLink } from 'apollo-upload-client';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { ApolloLink, Observable } from 'apollo-link';
import { onError } from 'apollo-link-error';
import { ERROR_MESSAGES } from '../utils/constants';

const BASE_URL = process.env.APOLLO_CLIENT;
const uploadLink = createUploadLink({ uri: BASE_URL + GRAPHQL });

const request = (operation, forward) => {
  const dependentFhirId = localStorage.getItem('dependentFhirId');
  const organisationName = localStorage.getItem('organizationName');
  const findCareToken = localStorage.getItem('findCareToken');
  const subscriberId = localStorage.getItem('findCareSubscriberId');

  // Check if we're on the find-care route
  const isFindCareRoute = window.location.pathname === '/find-care';

  // Public flow: on find-care with no token and no subscriberId — uses unauth APIs, no Bearer token
  const isPublicFlow = isFindCareRoute && !findCareToken && !subscriberId;

  const header = dependentFhirId ? { fetch_id: dependentFhirId } : {};
  header['time_zone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (isPublicFlow) {
    // Unauth APIs — bactpa headers, no authorization header
    header['org_name'] = 'bactpa';
    header['domain'] = 'bactpa';
    operation.setContext({ headers: { ...header } });
    return forward(operation);
  } else if (findCareToken && isFindCareRoute) {
    // Partner flow - may include subscriber_id if available
    header['org_name'] = 'bactpa';
    header['domain'] = 'bactpa';
    if (subscriberId) {
      header['subscriber_id'] = subscriberId;
    }
  } else {
    // Regular authenticated flow
    header['org_name'] = organisationName;
    header['domain'] = 'DEMO';
  }

  operation.setContext(({ headers = header }) => {
    // Only use partner token when on /find-care route, otherwise use regular auth token
    const token = (findCareToken && isFindCareRoute)
      ? findCareToken
      : auth.getAccessToken();

    return {
      headers: {
        ...headers,
        authorization: `Bearer ${token}`,
      },
    };
  });
  return forward(operation);
};

const requestLink = new ApolloLink(
  (operation, forward) =>
    new Observable((observer) => {
      let handle;
      Promise.resolve(operation)
        .then((oper) => request(oper, forward))
        .then(() => {
          handle = forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          });
        })
        .catch(observer.error.bind(observer));

      return () => {
        if (handle) {
          handle.unsubscribe();
        }
      };
    }),
);

const errorLink = onError(({ graphQLErrors }) => {
  if (graphQLErrors && graphQLErrors[0].message) {
    if (
      graphQLErrors[0].message.startsWith('401') ||
      graphQLErrors[0].message.includes('UNAUTHORIZED') ||
      graphQLErrors[0].message.includes('Not authorized')
    ) {
      auth.logout(true);
    }
  }
});

const link = errorLink.concat(uploadLink);

export default new ApolloClient({
  link: ApolloLink.from([requestLink, link]),
  cache: new InMemoryCache(),
});
